import React, { Suspense, useCallback, useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { useTranslation } from '@lyearn/i18n';
import { ChevronDownOutline, DragFill } from '@lyearn/icons';
import { ActionMenu, ActionMenuOverrides } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import { Box, Skeleton, Stack, Textarea, Touchable, Typography } from '@lyearn/ui';
import { DropOverContainer } from '@/components/DragDrop';
import { ExerciseQuestionInput, ResourceResponseTypeEnum } from '@/types/schema';

import { INPUT_CHARACTER_LIMIT, INPUT_ROW_LIMIT } from '../consts';
import ImageHotspotUploader from '../ImageHotspotUploader';
import LikertConfigEditor from '../LikertConfigEditor';
import SelectFormat from '../SelectFormat';
import { picklistStyles } from '../SelectFormat/styles';
import { QuestionItemActions, QuestionProps } from '../types';
import { getActionMenuItems } from '../utils';
import { draggableCursorStyle } from './styles';

const ACTION_MENU_OVERRIDES: ActionMenuOverrides = { PopoverContent: { position: 'left-start' } };

const INPUT_PROPS = { className: '!p-0', maxLength: INPUT_CHARACTER_LIMIT };

const SELECT_FORMAT_CLASSES = {
  endAdornment: 'w-16 h-16',
  input: '!ms-2 !me-2',
  inputRoot: 'ps-6 !pe-6 py-[0.5rem] !min-h-[3.2rem] h-32',
  paper: 'rounded-8 shadow-01 border border-divider my-8 w-[23rem]',
};

function Question(props: QuestionProps) {
  const {
    error,
    index,
    item,
    onDuplicate,
    onDelete,
    responseFormats,
    draggingItem,
    onDrop,
    onChangeQuestion,
  } = props;
  const { t } = useTranslation();
  const [isDraggable, { on, off }] = useToggle();
  const actions = useMemo(() => getActionMenuItems(t, item), [item, t]);

  const onAction = useCallback(
    (action: QuestionItemActions) => {
      switch (action) {
        case QuestionItemActions.Duplicate:
          onDuplicate(index);
          break;
        case QuestionItemActions.RemoveImage:
          onChangeQuestion(index, { ...item, medias: [], imageHotspots: [] });
          break;
        case QuestionItemActions.Delete:
          onDelete(index);
          break;
      }
    },
    [index, item, onChangeQuestion, onDelete, onDuplicate],
  );

  const onChangeText = useCallback(
    (text: string) => {
      onChangeQuestion(index, { ...item, text });
    },
    [index, item, onChangeQuestion],
  );

  const onFormatChange = useCallback(
    (responseType: ExerciseQuestionInput['responseType']) => {
      onChangeQuestion(index, {
        ...item,
        responseType,
        medias: [],
        imageHotspots: [],
        customRatingComponent: undefined,
      });
    },
    [index, item, onChangeQuestion],
  );

  const QuestionInput = (
    <Box
      className={cn('rounded-8 border border-border ps-20 pe-16 pt-16 pb-20 space-y-8', {
        'border-border-error-dark': !!error,
      })}>
      <Stack alignItems="center" className="w-full" justifyContent="between">
        <Typography className="cursor-default" color="text-text-secondary" variant="body-short-02">
          {t('Exercise:Creator.QuestionIndex', { index: index + 1 })}
        </Typography>
        <SelectFormat
          className={picklistStyles}
          classes={SELECT_FORMAT_CLASSES}
          formats={responseFormats}
          popupIcon={<ChevronDownOutline className="!h-16 !w-16" />}
          selectedValue={item.responseType}
          size="medium"
          onChange={onFormatChange}
        />
      </Stack>

      <Textarea
        className="w-full pe-4 border-none rounded-none"
        inputProps={INPUT_PROPS}
        maxRows={INPUT_ROW_LIMIT}
        minRows={INPUT_ROW_LIMIT}
        placeholder={t('Exercise:Creator.QuestionPlaceholder')}
        value={item.text}
        onChangeText={onChangeText}
      />

      {item.responseType === ResourceResponseTypeEnum.ImageHotspot ? (
        <ImageHotspotUploader index={index} question={item} onChangeQuestion={onChangeQuestion} />
      ) : null}
      {item.responseType === ResourceResponseTypeEnum.Likert ? (
        <Suspense fallback={<Skeleton height={162} variant="rounded" width="100%" />}>
          <LikertConfigEditor index={index} question={item} onChangeQuestion={onChangeQuestion} />
        </Suspense>
      ) : null}
    </Box>
  );

  return (
    <DropOverContainer
      draggable={isDraggable}
      onDragEnd={() => {
        draggingItem.current = undefined;
      }}
      onDragStart={() => {
        draggingItem.current = index;
      }}
      onDrop={() => onDrop(index)}>
      <Stack className="group relative -ms-36">
        <Box className="pe-12">
          <ActionMenu
            items={actions}
            minWidth={17.6}
            overrides={ACTION_MENU_OVERRIDES}
            onAction={onAction}>
            <Touchable>
              <DragFill
                className={cn(
                  'mt-12 h-24 w-24 fill-current text-icon-placeholder opacity-0 hover:text-icon-active group-hover:opacity-100',
                  { [draggableCursorStyle]: isDraggable },
                )}
                onMouseEnter={on}
                onMouseLeave={off}
              />
            </Touchable>
          </ActionMenu>
        </Box>
        <Box className="flex-1">
          {QuestionInput}
          {error ? (
            <Typography className="mt-8 ms-8" color="text-text-error" variant="body-short-03">
              {error}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </DropOverContainer>
  );
}

export default React.memo(Question);
